import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Eye, FilePen } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { RowActions } from "@/components/ui/RowActions";
import { AttachmentCell, type AttachmentRef } from "@/components/ui/AttachmentCell";
import { FilePreviewModal } from "@/components/ui/FilePreviewModal";
import { Button } from "@/components/ui/button";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import {
  fetchTrinhKy,
  fetchKyDuyet,
  fetchKyNhay,
  fetchBanHanh,
  fetchThaoLuan,
  fetchDonVi,
  fetchDocumentTypes,
} from "@/features/text-sign/api";
import { stateLabel } from "@/features/text-sign/helpers";
import { api } from "@/lib/api/client";
import type { TextSignSummary, TextSignListResponse } from "@pkhoffice/shared";

export type ListKind = "trinh-ky" | "ky-duyet" | "ky-nhay" | "ban-hanh" | "thao-luan" | "don-vi";

const TITLES: Record<ListKind, { title: string; breadcrumb: string; subtitle: string }> = {
  "trinh-ky": {
    title: "Trình Ký",
    breadcrumb: "Văn bản Ký điện tử › Trình Ký",
    subtitle: "Văn bản bạn đã khởi tạo và trình ký",
  },
  "ky-duyet": {
    title: "Ký duyệt",
    breadcrumb: "Văn bản Ký điện tử › Ký duyệt",
    subtitle: "Văn bản đang chờ bạn ký duyệt",
  },
  "ky-nhay": {
    title: "Ký nháy",
    breadcrumb: "Văn bản Ký điện tử › Ký nháy",
    subtitle: "Văn bản đang chờ bạn ký nháy",
  },
  "ban-hanh": {
    title: "Ban Hành",
    breadcrumb: "Văn bản Ký điện tử › Ban Hành",
    subtitle: "Văn bản đã ký xong, văn thư ban hành",
  },
  "thao-luan": {
    title: "Thảo luận văn bản",
    breadcrumb: "Văn bản Ký điện tử › Thảo luận",
    subtitle: "Văn bản bạn tham gia + có trao đổi nội dung",
  },
  "don-vi": {
    title: "Văn bản đơn vị",
    breadcrumb: "Văn bản Ký điện tử › Đơn vị",
    subtitle: "Tất cả văn bản của phòng ban bạn",
  },
};

const FETCHERS: Record<
  ListKind,
  (p?: Record<string, string | number | undefined>) => Promise<TextSignListResponse>
> = {
  "trinh-ky": fetchTrinhKy,
  "ky-duyet": fetchKyDuyet,
  "ky-nhay": fetchKyNhay,
  "ban-hanh": fetchBanHanh,
  "thao-luan": fetchThaoLuan,
  "don-vi": fetchDonVi,
};

// State options per menu kind. OLD shows tp.STATE (0/4/2) for ký-duyệt / ký-nháy,
// and t.STATE for trình-ký / ban-hành / thảo-luận / đơn-vị.
const STATE_OPTIONS: Record<ListKind, { value: number; label: string }[]> = {
  "trinh-ky": [
    { value: 0, label: "Nháp" },
    { value: 1, label: "Đang ký" },
    { value: 2, label: "Bị từ chối" },
    { value: 3, label: "Chờ ban hành" },
    { value: 4, label: "Đã ban hành" },
  ],
  "ky-duyet": [
    { value: 0, label: "Chưa xử lý" },
    { value: 2, label: "Đã từ chối" },
    { value: 4, label: "Đã ký" },
  ],
  "ky-nhay": [
    { value: 0, label: "Chưa ký nháy" },
    { value: 2, label: "Đã từ chối" },
    { value: 4, label: "Đã ký nháy" },
  ],
  "ban-hanh": [
    { value: 3, label: "Chờ ban hành" },
    { value: 4, label: "Đã ban hành" },
  ],
  "thao-luan": [
    { value: 1, label: "Đang ký" },
    { value: 3, label: "Chờ ban hành" },
    { value: 4, label: "Đã ban hành" },
  ],
  "don-vi": [
    { value: 1, label: "Đang ký" },
    { value: 3, label: "Chờ ban hành" },
    { value: 4, label: "Đã ban hành" },
  ],
};

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 90);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { fromDate: fmt(from), toDate: fmt(to) };
}

function buildColumns(
  kind: ListKind,
  page: number,
  pageSize: number,
  onOpen: (r: TextSignSummary) => void,
  onPreview: (f: AttachmentRef) => void,
): Column<TextSignSummary>[] {
  const stt: Column<TextSignSummary> = {
    key: "stt",
    header: "STT",
    width: "48px",
    align: "center",
    render: (_r, i) => (
      <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
    ),
  };
  const title: Column<TextSignSummary> = {
    key: "title",
    header: "Tên văn bản",
    render: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.title ?? "—"}</div>
        <div className="text-xs text-muted-foreground">
          {r.code ?? ""} {r.registerNumber ? `· ${r.registerNumber}` : ""}
        </div>
      </div>
    ),
  };
  const creator: Column<TextSignSummary> = {
    key: "creator",
    header: "Người khởi tạo",
    width: "220px",
    render: (r) => (
      <div>
        <div className="text-sm">{r.creatorName ?? "—"}</div>
        <div className="text-xs text-muted-foreground truncate">{r.createGroupName ?? ""}</div>
      </div>
    ),
  };
  /** Người ký cuối trong luồng — lưới Thảo luận văn bản của hệ cũ có cột này. */
  const signer: Column<TextSignSummary> = {
    key: "signer",
    header: "Người thực hiện",
    width: "180px",
    render: (r) => r.signerName ?? "—",
  };
  const date: Column<TextSignSummary> = {
    key: "date",
    header: "Ngày tạo",
    width: "140px",
    render: (r) => <span className="tabular-nums text-sm">{r.createDate ?? "—"}</span>,
  };
  const publishDate: Column<TextSignSummary> = {
    key: "publishDate",
    header: "Ngày ban hành",
    width: "140px",
    render: (r) => <span className="tabular-nums text-sm">{r.publishDate ?? "—"}</span>,
  };
  const state: Column<TextSignSummary> = {
    key: "state",
    // Hệ cũ đặt tên cột theo màn: ba màn duyệt/nháy/ban hành gọi là "Trạng thái
    // xử lý", còn lại là "Trạng thái văn bản" — giống hệt nhãn ô lọc bên dưới.
    header:
      kind === "ky-duyet" || kind === "ky-nhay" || kind === "ban-hanh"
        ? "Trạng thái xử lý"
        : "Trạng thái văn bản",
    width: "130px",
    render: (r) => {
      const s = stateLabel(r.state);
      return <StatusBadge variant={s.variant}>{s.label}</StatusBadge>;
    },
  };
  const signLevel: Column<TextSignSummary> = {
    key: "signLevel",
    header: "Cấp ký",
    width: "80px",
    align: "center",
    render: (r) => <span className="tabular-nums text-sm">{r.signLevel ?? "—"}</span>,
  };
  // Ba cột dưới đây hệ cũ có trên mọi lưới ký điện tử nhưng hệ mới còn thiếu.
  const action: Column<TextSignSummary> = {
    key: "action",
    header: "Thao tác",
    width: "90px",
    align: "center",
    render: (r) => (
      <RowActions actions={[{ label: "Xem chi tiết", icon: Eye, onClick: () => onOpen(r) }]} />
    ),
  };
  const priority: Column<TextSignSummary> = {
    key: "priority",
    header: "Độ khẩn",
    width: "110px",
    render: (r) => r.priorityName ?? "—",
  };
  const docType: Column<TextSignSummary> = {
    key: "type",
    header: "Thể thức",
    width: "140px",
    render: (r) => r.typeName ?? "—",
  };
  const mainFile: Column<TextSignSummary> = {
    key: "mainFile",
    header: "File ký",
    width: "200px",
    render: (r) => (
      <AttachmentCell
        files={r.mainFile ? [{ ...r.mainFile, source: "attach" }] : null}
        onOpen={onPreview}
      />
    ),
  };
  const receiveDate: Column<TextSignSummary> = {
    key: "receiveDate",
    header: "Ngày nhận",
    width: "140px",
    render: (r) => <span className="tabular-nums text-sm">{r.receiveDate ?? "—"}</span>,
  };
  const org: Column<TextSignSummary> = {
    key: "org",
    header: "Đơn vị trình ký",
    width: "200px",
    render: (r) => r.createGroupName ?? "—",
  };

  switch (kind) {
    // Bộ cột theo đúng lưới hệ cũ của từng màn.
    case "trinh-ky":
      return [stt, action, title, priority, docType, state, date, mainFile];
    case "ky-duyet":
    case "ky-nhay":
      return [stt, action, title, priority, docType, state, org, receiveDate, signLevel, mainFile];
    case "ban-hanh":
      return [stt, action, title, priority, docType, state, receiveDate, mainFile];
    case "thao-luan":
      // Hệ cũ: STT, Tên văn bản, Thể thức, Người tạo, Người thực hiện,
      // Trạng thái văn bản, Ngày tạo, File ký.
      return [
        stt,
        title,
        docType,
        { ...creator, header: "Người tạo" },
        signer,
        state,
        date,
        mainFile,
      ];
    case "don-vi":
      return [stt, action, title, priority, docType, state, date, publishDate, mainFile];
  }
}

export function TextSignListPage({ kind }: { kind: ListKind }) {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get("mode") ?? undefined;
  const isPendingMode = mode === "pending" && (kind === "ky-duyet" || kind === "ky-nhay");
  const { fromDate: defFrom, toDate: defTo } = defaultDateRange();

  // filter form state (what the user is editing)
  const [formQ, setFormQ] = useState("");
  const [formTypeId, setFormTypeId] = useState<string>("");
  const [formState, setFormState] = useState<string>("");
  const [formFrom, setFormFrom] = useState(defFrom);
  const [formTo, setFormTo] = useState(defTo);

  // applied filter state (what the query uses)
  const [applied, setApplied] = useState({
    q: "",
    typeId: "",
    state: "",
    fromDate: defFrom,
    toDate: defTo,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [previewFile, setPreviewFile] = useState<AttachmentRef | null>(null);

  useEffect(() => {
    // Reset applied filters on menu kind change
    const { fromDate, toDate } = defaultDateRange();
    setFormQ("");
    setFormTypeId("");
    setFormState("");
    setFormFrom(fromDate);
    setFormTo(toDate);
    setApplied({ q: "", typeId: "", state: "", fromDate, toDate });
    setPage(1);
  }, [kind]);

  const docTypes = useQuery({
    queryKey: ["text-sign-doc-types"],
    queryFn: fetchDocumentTypes,
    staleTime: 5 * 60_000,
  });

  const params = {
    page,
    pageSize: 25,
    q: applied.q || undefined,
    typeId: applied.typeId ? Number(applied.typeId) : undefined,
    state: applied.state !== "" ? Number(applied.state) : undefined,
    fromDate: applied.fromDate || undefined,
    toDate: applied.toDate || undefined,
    mode: isPendingMode ? "pending" : undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["text-sign", kind, params],
    queryFn: () => FETCHERS[kind](params),
    staleTime: 20_000,
  });

  const columns = buildColumns(
    kind,
    page,
    pageSize,
    (r) => nav(`/signs/${r.textId}`),
    setPreviewFile,
  );

  const onSubmit = () => {
    setApplied({
      q: formQ.trim(),
      typeId: formTypeId,
      state: formState,
      fromDate: formFrom,
      toDate: formTo,
    });
    setPage(1);
  };

  const onExport = async () => {
    const qs = new URLSearchParams();
    qs.set("pageSize", "5000");
    if (applied.q) qs.set("q", applied.q);
    if (applied.typeId) qs.set("typeId", applied.typeId);
    if (applied.state) qs.set("state", applied.state);
    if (applied.fromDate) qs.set("fromDate", applied.fromDate);
    if (applied.toDate) qs.set("toDate", applied.toDate);
    const res = await api.get(`/text-sign/${kind}/export?${qs.toString()}`, {
      responseType: "blob",
    });
    const blob = res.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `text-sign-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  return (
    <div>
      <PageHeader
        title={TITLES[kind].title}
        subtitle={TITLES[kind].subtitle}
        breadcrumb={TITLES[kind].breadcrumb}
        actions={
          kind === "trinh-ky" ? (
            <Button variant="default" size="md" onClick={() => nav("/signs/new")}>
              <FilePen className="h-4 w-4 mr-1.5" /> Soạn văn bản
            </Button>
          ) : undefined
        }
      />

      {isPendingMode && (
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-300 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            <FilePen className="h-3.5 w-3.5" strokeWidth={1.75} />
            Chỉ hiển thị văn bản đang chờ bạn ký
          </span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("mode");
              setSearchParams(next, { replace: true });
            }}
          >
            Bỏ lọc
          </button>
        </div>
      )}

      <SearchPanel
        onSubmit={onSubmit}
        onExport={onExport}
        fields={[
          {
            label: "Thông tin văn bản",
            control: (
              <input
                className={filterInputClass}
                placeholder="Nhập mã văn bản, tên văn bản"
                value={formQ}
                onChange={(e) => setFormQ(e.target.value)}
              />
            ),
          },
          {
            // Hệ cũ ghi rõ hai đầu khoảng: "Ngày tạo từ" … "đến".
            label: "Ngày tạo từ",
            required: true,
            control: (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className={filterInputClass}
                  value={formFrom}
                  onChange={(e) => setFormFrom(e.target.value)}
                />
                <span className="text-muted-foreground text-sm">đến</span>
                <input
                  type="date"
                  className={filterInputClass}
                  value={formTo}
                  onChange={(e) => setFormTo(e.target.value)}
                />
              </div>
            ),
          },
          {
            label: "Thể thức",
            control: (
              <select
                className={filterInputClass}
                value={formTypeId}
                onChange={(e) => setFormTypeId(e.target.value)}
              >
                <option value="">—— Tất cả ——</option>
                {docTypes.data?.map((t) => (
                  <option key={t.typeId} value={t.typeId}>
                    {t.name ?? `Loại ${t.typeId}`}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label:
              kind === "ky-duyet" || kind === "ky-nhay" || kind === "ban-hanh"
                ? "Trạng thái xử lý"
                : "Trạng thái văn bản",
            control: (
              <select
                className={filterInputClass}
                value={formState}
                onChange={(e) => setFormState(e.target.value)}
              >
                <option value="">—— Tất cả ——</option>
                {STATE_OPTIONS[kind].map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ),
          },
        ]}
        extra={
          data && (
            <span className="text-xs text-muted-foreground">
              {data.total.toLocaleString("vi-VN")} văn bản
            </span>
          )
        }
      />

      <DataTable<TextSignSummary>
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.textId}
        onRowClick={(r) => nav(`/signs/${r.textId}`)}
        isLoading={isLoading}
        empty="Không có văn bản nào"
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
      <FilePreviewModal
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
}
